import { useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/usePermissions';
import type { NavRequiredPermission, NavSubItem } from '@/layout/nav/NavMain';
import { TabNav } from './TabNav';
import { resolveActiveTab } from './resolveActiveTab';

export interface ConfigTabItem {
  value: string;
  labelKey: string;
  to: string;
  requiredPermission?: NavRequiredPermission;
}

/** Derives tabs from sidebar nav children so both stay in sync. */
export function tabsFromNavItems(items: NavSubItem[]): ConfigTabItem[] {
  return items.map((item) => ({
    value: item.url.split('/').pop() ?? '',
    labelKey: item.title,
    to: `/$slug${item.url}`,
    requiredPermission: item.requiredPermission
  }));
}

/**
 * Config-driven wrapper around the dumb TabNav: filters tabs by permission,
 * derives the active tab from the current pathname, and translates labels.
 */
export function ConfigTabNav({ tabs }: { tabs: ConfigTabItem[] }) {
  const { t } = useTranslation();
  const location = useLocation();
  const slug = useAuthStore().slug();
  const { can } = usePermissions();

  const visibleTabs = tabs.filter(
    (tab) =>
      !tab.requiredPermission ||
      can(tab.requiredPermission.subject, tab.requiredPermission.action ?? 'read')
  );

  const activeTab = resolveActiveTab(visibleTabs, location.pathname, slug);

  return (
    <TabNav
      tabs={visibleTabs.map((tab) => ({
        value: tab.value,
        label: t(tab.labelKey),
        to: tab.to
      }))}
      activeTab={activeTab}
      slug={slug}
    />
  );
}
